package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.CanvasCommentThreadDto
import dev.lingvo.shared.CanvasMentionDto
import dev.lingvo.shared.CanvasReviewDto
import dev.lingvo.shared.CanvasSlotMarkDto
import dev.lingvo.shared.CreateCanvasCommentRequest
import dev.lingvo.shared.CreateScreenshotReviewLinkRequest
import dev.lingvo.shared.CreatedScreenshotReviewLinkDto
import dev.lingvo.shared.PutCanvasSlotMarkRequest
import dev.lingvo.shared.ScreenshotReviewLinkDto
import dev.lingvo.shared.SetCanvasThreadStateRequest
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.ApiConfig
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.i18n.tr
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.withTimeout
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import dev.lingvo.site.i18n.t

/** Review transport lives with the screenshot UI and never puts comments or capability secrets in diagnostics. */
internal object ScreenshotReviewApi {
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }
    private fun base(slug: String, platform: StorePlatform) = "/projects/${encodeURIComponent(slug)}/screenshots/${platform.id}"
    private fun versionBase(slug: String, platform: StorePlatform, version: Int) = "${base(slug, platform)}/design/versions/$version"

    suspend fun review(slug: String, platform: StorePlatform, version: Int): CanvasReviewDto =
        decode(send("GET", "${versionBase(slug, platform, version)}/review"))
    suspend fun members(slug: String, platform: StorePlatform, version: Int): List<CanvasMentionDto> =
        decode(send("GET", "${versionBase(slug, platform, version)}/review/members"))
    suspend fun comment(slug: String, platform: StorePlatform, version: Int, request: CreateCanvasCommentRequest): CanvasCommentThreadDto =
        decode(send("POST", "${versionBase(slug, platform, version)}/comments", json.encodeToString(request)))
    suspend fun state(slug: String, platform: StorePlatform, version: Int, threadId: String, request: SetCanvasThreadStateRequest): CanvasCommentThreadDto =
        decode(send("POST", "${versionBase(slug, platform, version)}/comments/${encodeURIComponent(threadId)}/state", json.encodeToString(request)))
    suspend fun mark(slug: String, platform: StorePlatform, version: Int, request: PutCanvasSlotMarkRequest): CanvasSlotMarkDto =
        decode(send("PUT", "${versionBase(slug, platform, version)}/marks", json.encodeToString(request)))
    suspend fun links(slug: String, platform: StorePlatform): List<ScreenshotReviewLinkDto> =
        decode(send("GET", "${base(slug, platform)}/review-links"))
    suspend fun createLink(slug: String, platform: StorePlatform, request: CreateScreenshotReviewLinkRequest): CreatedScreenshotReviewLinkDto =
        decode(send("POST", "${base(slug, platform)}/review-links", json.encodeToString(request)))
    suspend fun revokeLink(slug: String, platform: StorePlatform, id: String) {
        send("POST", "${base(slug, platform)}/review-links/${encodeURIComponent(id)}/revoke")
    }

    private inline fun <reified T> decode(raw: String): T = try { json.decodeFromString<T>(raw) }
        catch (_: Throwable) { throw ApiException(502, "invalid_response", failureMessage()) }

    private suspend fun send(method: String, path: String, body: String? = null): String {
        val controller: dynamic = js("typeof AbortController === 'undefined' ? null : new AbortController()")
        try {
            return withTimeout(20_000) {
                val headers = Headers().apply {
                    set("Accept", "application/json")
                    if (body != null) set("Content-Type", "application/json")
                    Session.token?.takeIf(String::isNotBlank)?.let { set("Authorization", "Bearer $it") }
                }
                val init = RequestInit(method = method, headers = headers, body = body)
                init.asDynamic().referrerPolicy = "no-referrer"
                init.asDynamic().cache = "no-store"
                init.asDynamic().credentials = "omit"
                init.asDynamic().redirect = "error"
                if (controller != null) init.asDynamic().signal = controller.signal
                val response = window.fetch("${ApiConfig.baseUrl}$path", init).await()
                if (!response.ok) throw ApiException(response.status.toInt(), "review_request_failed", failureMessage())
                val raw = response.text().await()
                if (raw.length > 4_194_304) throw ApiException(502, "invalid_response", failureMessage())
                raw
            }
        } catch (_: TimeoutCancellationException) { throw ApiException(0, "request_timeout", failureMessage()) }
        catch (cancelled: CancellationException) { throw cancelled }
        catch (failure: ApiException) { throw failure }
        catch (_: Throwable) { throw ApiException(0, "review_request_failed", failureMessage()) }
        finally { controller?.abort() }
    }

    private fun failureMessage() = tr("app.projects.slug.screenshots.screenshot_review_api.the_review_could_not_be_updated", "The review could not be updated. Refresh to check current access and content, then retry.")
}

private external fun encodeURIComponent(value: String): String
